
  import Teleport from 'vue2-teleport'
  import BaseTooltip from '@/components/BaseTooltip.vue'
  import {
    isClientBlacklisted,
    isClientCompany,
    isLightCategoryColor,
  } from '@/utils/clientCategories'

  const POPUP_GAP = 4
  const POPUP_PAD = 8
  const CLOSE_DELAY_MS = 120

  export default {
    name: 'ClientMarks',

    components: { BaseTooltip, Teleport },

    props: {
      client: {
        type: Object,
        default: null,
      },
      showCompany: {
        type: Boolean,
        default: false,
      },
      dense: {
        type: Boolean,
        default: false,
      },
      fill: {
        type: Boolean,
        default: false,
      },
      overflowPopup: {
        type: Boolean,
        default: true,
      },
      overflowPlain: {
        type: Boolean,
        default: false,
      },
      maxVisibleCategories: {
        type: Number,
        default: null,
      },
    },

    data() {
      return {
        visibleIds: null,
        overflow: {
          open: false,
          style: {},
          closeTimer: null,
        },
        observer: null,
        measureTimer: null,
      }
    },

    computed: {
      companyAvailable() {
        return this.showCompany && isClientCompany(this.client)
      },
      blacklistAvailable() {
        return isClientBlacklisted(this.client)
      },
      categories() {
        return Array.isArray(this.client?.clientCategories)
          ? this.client.clientCategories
          : []
      },
      canOpenPopup() {
        return this.overflowPopup && !this.overflowPlain
      },
      allMarkItems() {
        const items = []

        if (this.companyAvailable) items.push({ kind: 'company' })
        if (this.blacklistAvailable) items.push({ kind: 'blacklist' })

        this.categories.forEach((category) => {
          items.push({ kind: 'category', category })
        })

        return items
      },
      cappedMarkItems() {
        if (this.maxVisibleCategories == null) return null

        return this.allMarkItems.slice(
          0,
          Math.max(0, this.maxVisibleCategories)
        )
      },
      showCompanyMark() {
        return this.cappedMarkItems
          ? this.cappedMarkItems.some((item) => item.kind === 'company')
          : this.companyAvailable
      },
      showBlacklistMark() {
        return this.cappedMarkItems
          ? this.cappedMarkItems.some((item) => item.kind === 'blacklist')
          : this.blacklistAvailable
      },
      visibleCategories() {
        if (this.cappedMarkItems) {
          return this.cappedMarkItems
            .filter((item) => item.kind === 'category')
            .map((item) => item.category)
        }

        if (!this.categories.length) return []
        if (!this.visibleIds) return this.categories

        const allowed = new Set(this.visibleIds)
        return this.categories.filter((tag) => allowed.has(tag.id))
      },
      hiddenCount() {
        if (this.cappedMarkItems) {
          return Math.max(
            0,
            this.allMarkItems.length - this.cappedMarkItems.length
          )
        }

        return Math.max(
          0,
          this.categories.length - this.visibleCategories.length
        )
      },
      overflowLabel() {
        return this.$t('pages.clients.categories.overflow', {
          count: this.hiddenCount,
        })
      },
      visible() {
        return (
          this.companyAvailable ||
          this.blacklistAvailable ||
          this.categories.length > 0
        )
      },
    },

    watch: {
      categories: {
        handler() {
          this.visibleIds = null
          this.queueMeasure()
        },
        deep: true,
      },
      maxVisibleCategories() {
        this.visibleIds = null
        this.queueMeasure()
      },
      visible(value) {
        if (!value) return
        this.$nextTick(this.observe)
      },
      'overflow.open'(value) {
        if (!value) return
        this.$nextTick(() => {
          this.positionPopup()
          this.$nextTick(this.positionPopup)
        })
      },
    },

    mounted() {
      this.$nextTick(() => {
        this.measure()
        this.observe()
      })
      window.addEventListener('scroll', this.onViewportChange, true)
      window.addEventListener('resize', this.onViewportChange)
      document.addEventListener('click', this.onDocumentClick, true)
    },

    beforeDestroy() {
      this.clearCloseTimer()
      if (this.measureTimer) clearTimeout(this.measureTimer)
      this.observer?.disconnect()
      window.removeEventListener('scroll', this.onViewportChange, true)
      window.removeEventListener('resize', this.onViewportChange)
      document.removeEventListener('click', this.onDocumentClick, true)
    },

    methods: {
      isLightCategoryColor,

      clearCloseTimer() {
        if (!this.overflow.closeTimer) return
        clearTimeout(this.overflow.closeTimer)
        this.overflow.closeTimer = null
      },

      openMore() {
        if (!this.canOpenPopup) return
        this.clearCloseTimer()
        this.overflow.open = true
      },

      closeMore() {
        this.clearCloseTimer()
        this.overflow.open = false
      },

      onMoreClick() {
        if (!this.canOpenPopup) return
        this.clearCloseTimer()
        this.overflow.open = !this.overflow.open
      },

      onDocumentClick(event) {
        if (!this.overflow.open) return

        const target = event.target
        if (
          this.$refs.more?.contains?.(target) ||
          this.$refs.popup?.contains?.(target)
        ) {
          return
        }

        this.closeMore()
      },

      scheduleCloseMore() {
        this.clearCloseTimer()
        this.overflow.closeTimer = setTimeout(() => {
          this.overflow.open = false
          this.overflow.closeTimer = null
        }, CLOSE_DELAY_MS)
      },

      onViewportChange() {
        this.queueMeasure()
        if (!this.overflow.open) return
        this.positionPopup()
      },

      queueMeasure() {
        if (this.measureTimer) clearTimeout(this.measureTimer)
        this.measureTimer = setTimeout(() => {
          this.measureTimer = null
          this.measure()
        }, 16)
      },

      positionPopup() {
        if (!this.overflow.open) return

        const anchor = this.$refs.more
        if (!anchor) return

        const rect = anchor.getBoundingClientRect()
        const popup = this.$refs.popup
        const popupWidth = popup?.offsetWidth || 224
        const popupHeight = popup?.offsetHeight || 0
        const vw = window.innerWidth
        const vh = window.innerHeight

        let left = rect.left
        if (left + popupWidth > vw - POPUP_PAD) {
          left = Math.max(POPUP_PAD, rect.right - popupWidth)
        }

        const openAbove =
          rect.top > vh * 0.55 ||
          (popupHeight &&
            rect.bottom + POPUP_GAP + popupHeight > vh - POPUP_PAD)

        const top = openAbove
          ? Math.max(POPUP_PAD, rect.top - (popupHeight || 0) - POPUP_GAP)
          : rect.bottom + POPUP_GAP

        this.overflow.style = {
          top: `${top}px`,
          left: `${left}px`,
          maxWidth: `${Math.min(288, vw - POPUP_PAD * 2)}px`,
          maxHeight: `${Math.max(80, vh - top - POPUP_PAD)}px`,
        }
      },

      observe() {
        this.observer?.disconnect()
        if (!this.$refs.root || typeof ResizeObserver === 'undefined') return

        this.observer = new ResizeObserver(() => this.queueMeasure())
        this.observer.observe(this.$refs.root)

        if (this.$refs.root.parentElement) {
          this.observer.observe(this.$refs.root.parentElement)
        }
      },

      availableWidth(root) {
        if (this.fill) {
          return root.clientWidth || root.parentElement?.clientWidth || 0
        }

        const parentWidth = root.parentElement?.clientWidth || 0
        if (parentWidth <= 0) return root.clientWidth || 0

        return Math.max(48, Math.floor(parentWidth * 0.55))
      },

      measure() {
        const root = this.$refs.root
        const probe = this.$refs.probe
        if (!root || !probe) return

        if (this.maxVisibleCategories != null) {
          this.visibleIds = this.visibleCategories.map((tag) => tag.id)
          return
        }

        if (!this.categories.length) {
          this.visibleIds = []
          return
        }

        const width = this.availableWidth(root)
        if (width <= 0) return

        const gap = parseFloat(getComputedStyle(probe).gap) || 0
        const fixed = [
          ...probe.querySelectorAll('.marks__company, .marks__blacklist'),
        ]
        const tagEls = [...probe.querySelectorAll('.marks__category')]
        const moreEl = probe.querySelector('.marks__more')
        const moreWidth = (moreEl?.offsetWidth || 28) + gap

        const fixedWidth =
          fixed.reduce((sum, node) => sum + node.offsetWidth, 0) +
          Math.max(0, fixed.length - 1) * gap

        const widths = tagEls.map((node) => node.offsetWidth)
        const totalTagsWidth = widths.reduce(
          (sum, tagWidth, index) =>
            sum + tagWidth + (index || fixed.length ? gap : 0),
          0
        )

        if (fixedWidth + totalTagsWidth <= width + 1) {
          this.visibleIds = this.categories.map((tag) => tag.id)
          return
        }

        const budget = width - moreWidth
        const selected = this.packTags(
          widths,
          fixedWidth,
          gap,
          fixed.length,
          budget
        )

        if (!selected.length) {
          const shortest = widths
            .map((tagWidth, index) => ({ tagWidth, index }))
            .sort((a, b) => a.tagWidth - b.tagWidth)

          for (const item of shortest) {
            const next = fixedWidth + (fixed.length ? gap : 0) + item.tagWidth
            if (next <= budget) {
              selected.push(item.index)
              break
            }
          }
        }

        this.visibleIds = selected.map((index) => this.categories[index].id)
      },

      packTags(widths, fixedWidth, gap, fixedCount, budget) {
        const selected = []
        let used = fixedWidth

        widths.forEach((tagWidth, index) => {
          const next =
            used + (fixedCount || selected.length ? gap : 0) + tagWidth
          if (next > budget) return

          used = next
          selected.push(index)
        })

        return selected
      },
    },
  }
